// src/modules/app-views/collaborators/pages/Document/tabs/ProfessionalTab/index.jsx
// *******************************************************************************
// PROFESSIONAL TAB
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************

// 1. React and hooks
import { useMemo } from 'react';

// 2. External libraries
import { Card, Col, Row } from 'antd';

// 3. Providers / Context
import { useDocumentContext } from 'components/core/DocumentProvider';

// 4. Components
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleDatePicker from 'components/SimpleComponents/SimpleDatePicker';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SystemUserLinkCard from './SystemUserLinkCard';

// 5. Services and stores
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************

const ProfessionalTab = () => {
  // ---------------------------------------------------------------------------
  // PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------

  const { viewMode } = useDocumentContext();
  const storedParameters = useZustand('app', 'parameters');
  const storedModule = useZustand('module', 'config');

  // ---------------------------------------------------------------------------
  // HOOKS & REFS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES I
  // ---------------------------------------------------------------------------

  const languageOptions = buildLanguageOptions();
  const departmentOptions = useMemo(
    () => buildParameterOptions(storedParameters, storedModule?.id, 'departments'),
    [storedParameters, storedModule?.id]
  );
  const positionOptions = useMemo(
    () => buildParameterOptions(storedParameters, storedModule?.id, 'positions'),
    [storedParameters, storedModule?.id]
  );

  // ---------------------------------------------------------------------------
  // LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------

  return (
    <>
      <Card title={translateX('professional_data')} className="mb-3">
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12} md={8}>
            <SimpleSelect
              name={['collaboratorFields', 'departmentUid']}
              label="department"
              options={departmentOptions}
              allowClear
              showSearch
              showAddButton={{ modal: 'parameters', tab: 'departments' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <SimpleSelect
              name={['collaboratorFields', 'positionUid']}
              label="position"
              options={positionOptions}
              allowClear
              showSearch
              showAddButton={{ modal: 'parameters', tab: 'positions' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <SimpleNumber
              name={['collaboratorFields', 'commissionRate']}
              label="commission_rate"
              percentage
              min={0}
              max={100}
            />
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12} md={8}>
            <SimpleDatePicker
              name={['collaboratorFields', 'admissionDate']}
              label="admission_date"
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <SimpleSelect
              name={['collaboratorFields', 'defaultLanguage']}
              label="default_language"
              options={languageOptions}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <SimpleInput
              name={['collaboratorFields', 'supervisor']}
              label="supervisor"
              disabled
            />
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <SimpleInput
              name={['collaboratorFields', 'workEmail']}
              label="work_email"
            />
          </Col>
          <Col xs={24} sm={12}>
            <SimpleInput
              name={['collaboratorFields', 'workPhone']}
              label="work_phone"
            />
          </Col>
        </Row>
      </Card>

      <SystemUserLinkCard />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************

export default ProfessionalTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************

function buildLanguageOptions() {
  return [
    { value: 'pt_BR', label: 'Português (Brasil)' },
    { value: 'es', label: 'Español' },
    { value: 'en_US', label: 'English' },
  ];
}

function buildParameterOptions(storedParameters, moduleId, category) {
  const items = storedParameters?.modules?.[moduleId]?.[category];
  if (!items || !Array.isArray(items)) return [];

  return items
    .filter(item => !item.removed)
    .map(item => ({
      value: item.uid,
      label: item.label,
      text: item.label,
    }));
}
